import { useEffect, useMemo, useState } from 'react';
import { Edit, Eye, FolderOpen, MinusCircle, Plus, Search, Trash2 } from 'lucide-react';
import { ERPUser } from '../../core/auth/types';
import { ExtraSupplierContact, SupplierRecord } from './types/supplierTypes';
import { deleteSupplier, emptySupplier, findSupplierDuplicates, listSuppliers, saveSupplier } from './services/supplierService';

const supplierTypes = [
  'airline',
  'cruise',
  'hotel',
  'dmc',
  'visa',
  'insurance',
  'transport',
  'guide',
  'transfer',
  'consolidator',
  'corporate',
  'payment_vendor',
  'other'
];

const departments = [
  'flight',
  'package',
  'cruise',
  'account',
  'general',
  'visa',
  'insurance',
  'transport'
];

function validateSupplier(form: Partial<SupplierRecord>) {
  if (!form.supplierName?.trim()) return 'Supplier Name is required.';
  if (!form.supplierType?.trim()) return 'Supplier Type is required.';
  if (!form.department?.trim()) return 'Department is required.';
  return '';
}

function formatContacts(contacts: ExtraSupplierContact[]) {
  if (!contacts.length) return '-';
  return contacts
    .map((item) => `${item.name || '-'} | ${item.phone || '-'} | ${item.email || '-'}`)
    .join(' || ');
}

function openSupplierDocuments() {
  window.history.pushState(null, '', '/documents?ownerType=supplier');
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export default function SupplierMasterPage({ user }: { user: ERPUser }) {
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [viewing, setViewing] = useState<SupplierRecord | null>(null);
  const [editing, setEditing] = useState<SupplierRecord | null>(null);
  const [form, setForm] = useState<Partial<SupplierRecord>>(() => emptySupplier());
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function loadSuppliers() {
    const rows = await listSuppliers();
    setSuppliers(rows);
  }

  useEffect(() => {
    loadSuppliers().catch((error) => {
      setMessage(error instanceof Error ? error.message : 'Unable to load suppliers.');
    });
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    return suppliers.filter((supplier) => {
      if (!q) return true;

      return [
        supplier.supplierName,
        supplier.supplierType,
        supplier.department,
        supplier.primaryContactName,
        supplier.phone,
        supplier.tollFreeNo,
        supplier.email,
        supplier.city,
        supplier.country,
        supplier.paymentTerms,
        supplier.status,
        supplier.supplierCode
      ].join(' ').toLowerCase().includes(q);
    });
  }, [suppliers, search]);

  function startAdd() {
    setEditing(null);
    setViewing(null);
    setMoreOpen(false);
    setMessage('');
    setForm(emptySupplier());
    setModalOpen(true);
  }

  function startEdit(supplier: SupplierRecord) {
    setEditing(supplier);
    setViewing(null);
    setMoreOpen(false);
    setMessage('');
    setForm(supplier);
    setModalOpen(true);
  }

  async function handleSave() {
    const validation = validateSupplier(form);

    if (validation) {
      setMessage(validation);
      return;
    }

    setBusy(true);

    try {
      if (!editing) {
        const duplicates = await findSupplierDuplicates(form);

        if (
          duplicates.length &&
          !window.confirm(`Possible duplicate supplier found: ${duplicates[0].supplierName}. Save anyway?`)
        ) {
          return;
        }
      }

      await saveSupplier(form, user);
      setModalOpen(false);
      await loadSuppliers();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to save supplier.');
    } finally {
      setBusy(false);
    }
  }

  function addContact() {
    setForm((current) => ({
      ...current,
      extraContacts: [
        ...(current.extraContacts || []),
        { name: '', phone: '', email: '' }
      ]
    }));
  }

  function updateContact(index: number, key: keyof ExtraSupplierContact, value: string) {
    setForm((current) => {
      const list = [...(current.extraContacts || [])];
      list[index] = { ...list[index], [key]: value };

      return {
        ...current,
        extraContacts: list
      };
    });
  }

  function removeContact(index: number) {
    setForm((current) => {
      const list = [...(current.extraContacts || [])];
      list.splice(index, 1);

      return {
        ...current,
        extraContacts: list
      };
    });
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Phase 4.2 + Phase 5.5C Repair</p>
          <h1>Supplier Master Final</h1>
          <p>
            Supplier profile, contacts, branch, bank details, payment terms, and document vault connection.
          </p>
        </div>

        <button className="btn primary" title="Add new supplier" onClick={startAdd}>
          <Plus size={18} /> Add Supplier
        </button>
      </div>

      {message && (
        <div className="error-box" style={{ marginBottom: 20 }}>
          {message}
        </div>
      )}

      <div className="toolbar">
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: 16, top: 18, color: '#66758a' }} />
          <input
            style={{ paddingLeft: 44 }}
            placeholder="Search supplier, type, phone, city, country"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      <section className="card table-card" style={{ marginTop: 20 }}>
        <table>
          <thead>
            <tr>
              <th>Supplier Name</th>
              <th>Type</th>
              <th>Primary Contact</th>
              <th>Phone</th>
              <th>Toll Free</th>
              <th>City</th>
              <th>Country</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="empty">
                  No suppliers found.
                </td>
              </tr>
            )}

            {filtered.map((supplier) => (
              <tr key={supplier.id}>
                <td>
                  <strong>{supplier.supplierName}</strong>
                  <br />
                  <small>{supplier.supplierCode}</small>
                </td>
                <td>{supplier.supplierType}</td>
                <td>{supplier.primaryContactName || '-'}</td>
                <td>{supplier.phone || '-'}</td>
                <td>{supplier.tollFreeNo || '-'}</td>
                <td>{supplier.city || '-'}</td>
                <td>{supplier.country || '-'}</td>
                <td className="actions">
                  <button title="View supplier full details" onClick={() => setViewing(supplier)}>
                    <Eye size={16} />
                  </button>

                  <button title="Edit supplier" onClick={() => startEdit(supplier)}>
                    <Edit size={16} />
                  </button>

                  <button title="View supplier documents" onClick={openSupplierDocuments}>
                    <FolderOpen size={16} />
                  </button>

                  <button
                    title="Delete supplier"
                    onClick={async () => {
                      if (window.confirm('Delete this supplier?')) {
                        await deleteSupplier(supplier.id);
                        await loadSuppliers();
                      }
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {modalOpen && (
        <div className="modal-backdrop">
          <div className="modal large">
            <div className="modal-head">
              <h2>{editing ? 'Edit Supplier' : 'Add Supplier'}</h2>
              <button className="icon-only" title="Close" onClick={() => setModalOpen(false)}>
                ×
              </button>
            </div>

            {message && (
              <div className="error-box" style={{ margin: '18px 30px' }}>
                {message}
              </div>
            )}

            <div className="form-section-title">Main Supplier Details</div>

            <div className="form-grid">
              <label>
                Supplier Name *
                <input
                  value={form.supplierName || ''}
                  onChange={(event) => setForm({ ...form, supplierName: event.target.value })}
                />
              </label>

              <label>
                Type *
                <select
                  value={form.supplierType || 'hotel'}
                  onChange={(event) => setForm({ ...form, supplierType: event.target.value })}
                >
                  {supplierTypes.map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </label>

              <label>
                Department *
                <select
                  value={form.department || 'package'}
                  onChange={(event) => setForm({ ...form, department: event.target.value })}
                >
                  {departments.map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </label>

              <label>
                Primary Contact
                <input
                  value={form.primaryContactName || ''}
                  onChange={(event) => setForm({ ...form, primaryContactName: event.target.value })}
                />
              </label>

              <label>
                Phone
                <input
                  value={form.phone || ''}
                  onChange={(event) => setForm({ ...form, phone: event.target.value })}
                />
              </label>

              <label>
                Toll Free No
                <input
                  value={form.tollFreeNo || ''}
                  onChange={(event) => setForm({ ...form, tollFreeNo: event.target.value })}
                />
              </label>

              <label>
                Email
                <input
                  value={form.email || ''}
                  onChange={(event) => setForm({ ...form, email: event.target.value })}
                />
              </label>

              <label>
                City
                <input
                  value={form.city || ''}
                  onChange={(event) => setForm({ ...form, city: event.target.value })}
                />
              </label>

              <label>
                Country
                <input
                  value={form.country || ''}
                  onChange={(event) => setForm({ ...form, country: event.target.value })}
                />
              </label>

              <label>
                Payment Terms
                <input
                  value={form.paymentTerms || ''}
                  onChange={(event) => setForm({ ...form, paymentTerms: event.target.value })}
                />
              </label>

              <label>
                Status
                <select
                  value={form.status || 'active'}
                  onChange={(event) => setForm({ ...form, status: event.target.value as SupplierRecord['status'] })}
                >
                  <option>active</option>
                  <option>inactive</option>
                  <option>hold</option>
                </select>
              </label>

              <label>
                Website
                <input
                  value={form.website || ''}
                  onChange={(event) => setForm({ ...form, website: event.target.value })}
                />
              </label>

              <label className="span-3">
                Address
                <input
                  value={form.address || ''}
                  onChange={(event) => setForm({ ...form, address: event.target.value })}
                />
              </label>

              <label className="span-3">
                Notes
                <textarea
                  value={form.notes || ''}
                  onChange={(event) => setForm({ ...form, notes: event.target.value })}
                />
              </label>
            </div>

            <div className="more-toggle">
              <button className="btn" title="Open extra supplier fields" onClick={() => setMoreOpen(!moreOpen)}>
                {moreOpen ? 'Hide More Details' : '+ More Details'}
              </button>
            </div>

            {moreOpen && (
              <>
                <div className="family-box">
                  <div className="family-head">
                    <h3>Additional Contact Persons</h3>
                    <button className="btn" title="Add another supplier contact" onClick={addContact}>
                      <Plus size={16} /> Add More
                    </button>
                  </div>

                  {(form.extraContacts || []).map((contact, index) => (
                    <div className="family-row" key={index}>
                      <input
                        placeholder="Name"
                        value={contact.name}
                        onChange={(event) => updateContact(index, 'name', event.target.value)}
                      />
                      <input
                        placeholder="Phone"
                        value={contact.phone}
                        onChange={(event) => updateContact(index, 'phone', event.target.value)}
                      />
                      <input
                        placeholder="Email"
                        value={contact.email}
                        onChange={(event) => updateContact(index, 'email', event.target.value)}
                      />
                      <button title="Remove contact person" onClick={() => removeContact(index)}>
                        <MinusCircle size={18} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="form-section-title">Branch Office + Bank Details</div>

                <div className="form-grid">
                  <label>
                    Branch Office Name
                    <input
                      value={form.branchOfficeName || ''}
                      onChange={(event) => setForm({ ...form, branchOfficeName: event.target.value })}
                    />
                  </label>

                  <label className="span-2">
                    Branch Office Address
                    <input
                      value={form.branchOfficeAddress || ''}
                      onChange={(event) => setForm({ ...form, branchOfficeAddress: event.target.value })}
                    />
                  </label>

                  <label className="span-3">
                    Bank Details
                    <textarea
                      value={form.bankDetails || ''}
                      onChange={(event) => setForm({ ...form, bankDetails: event.target.value })}
                    />
                  </label>

                  <label className="span-3">
                    Internal Notes
                    <textarea
                      value={form.internalNotes || ''}
                      onChange={(event) => setForm({ ...form, internalNotes: event.target.value })}
                    />
                  </label>
                </div>
              </>
            )}

            <div className="modal-foot">
              <button className="btn" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="btn primary" disabled={busy} onClick={handleSave}>
                {busy ? 'Saving...' : 'Save Supplier'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewing && (
        <div className="modal-backdrop">
          <div className="modal large">
            <div className="modal-head">
              <h2>{viewing.supplierName}</h2>
              <button className="icon-only" title="Close" onClick={() => setViewing(null)}>
                ×
              </button>
            </div>

            <div className="form-section-title">All Supplier Details</div>

            <div className="form-grid">
              <div><b>Supplier Code</b><p>{viewing.supplierCode || '-'}</p></div>
              <div><b>Type</b><p>{viewing.supplierType || '-'}</p></div>
              <div><b>Department</b><p>{viewing.department || '-'}</p></div>
              <div><b>Primary Contact</b><p>{viewing.primaryContactName || '-'}</p></div>
              <div><b>Phone</b><p>{viewing.phone || '-'}</p></div>
              <div><b>Toll Free No</b><p>{viewing.tollFreeNo || '-'}</p></div>
              <div><b>Email</b><p>{viewing.email || '-'}</p></div>
              <div><b>City</b><p>{viewing.city || '-'}</p></div>
              <div><b>Country</b><p>{viewing.country || '-'}</p></div>
              <div><b>Payment Terms</b><p>{viewing.paymentTerms || '-'}</p></div>
              <div><b>Status</b><p>{viewing.status || '-'}</p></div>
              <div><b>Website</b><p>{viewing.website || '-'}</p></div>
              <div className="span-3"><b>Address</b><p>{viewing.address || '-'}</p></div>
              <div className="span-3"><b>Notes</b><p>{viewing.notes || '-'}</p></div>
              <div className="span-3"><b>Additional Contacts</b><p>{formatContacts(viewing.extraContacts)}</p></div>
              <div><b>Branch Office Name</b><p>{viewing.branchOfficeName || '-'}</p></div>
              <div className="span-2"><b>Branch Address</b><p>{viewing.branchOfficeAddress || '-'}</p></div>
              <div className="span-3"><b>Bank Details</b><p>{viewing.bankDetails || '-'}</p></div>
              <div className="span-3"><b>Internal Notes</b><p>{viewing.internalNotes || '-'}</p></div>
            </div>

            <div className="modal-foot">
              <button className="btn" title="View supplier documents" onClick={openSupplierDocuments}>
                View Documents
              </button>
              <button
                className="btn primary"
                title="Edit this supplier"
                onClick={() => {
                  setViewing(null);
                  startEdit(viewing);
                }}
              >
                Edit Supplier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
